import { useEffect, useState, type FormEvent } from 'react';
import {
  recordValue,
  useApplyDnsChange,
  useDomainDns,
  useDomainDnsChanges,
  usePreviewDnsChange,
  type DnsAction,
  type DnsRecordView,
  type EditableRecordType,
  type EmailPresetId,
} from '@/data/domain-dns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { TableScroll } from '@/components/ui/table-scroll';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/auth/api';
import { formatRelative } from '@/lib/format';

const EMAIL_PRESET_LABELS: Record<EmailPresetId, string> = {
  'google-workspace': 'Google Workspace',
  'microsoft-365': 'Microsoft 365',
  other: 'Other provider',
};

const RECORD_TYPES: EditableRecordType[] = ['A', 'AAAA', 'CNAME', 'TXT', 'MX'];

/**
 * Unit B — DNS & email management on a managed domain, mounted in the
 * managed-domain panel. Your domain, fully yours: email presets, custom
 * records, and a change history. Every write is preview-then-apply.
 */
export function ManagedDomainDns({
  fleetId,
  zone,
  canEdit,
}: {
  fleetId: string;
  zone: string;
  canEdit: boolean;
}) {
  const overview = useDomainDns(fleetId, zone);
  const [pending, setPending] = useState<{ action: DnsAction; title: string } | null>(null);

  return (
    <div className="space-y-6 border-t border-breaker-line pt-5">
      <div>
        <h3 className="text-sm font-semibold text-breaker-ink">Email &amp; DNS</h3>
        <p className="text-xs text-breaker-mute mt-0.5">
          Your domain, fully yours — set up email and edit records right here. No DNS
          homework; we&rsquo;ll show you exactly what changes before anything goes live.
        </p>
      </div>

      {overview.isLoading ? (
        <div className="py-6 flex justify-center">
          <Spinner size="sm" />
        </div>
      ) : overview.isError ? (
        <p className="text-sm text-breaker-red">Couldn&rsquo;t load your domain&rsquo;s records.</p>
      ) : overview.data ? (
        <>
          <EmailSection
            posture={overview.data.email}
            canEdit={canEdit}
            onChoose={(action, title) => setPending({ action, title })}
          />
          <RecordsSection
            records={overview.data.records}
            zone={zone}
            canEdit={canEdit}
            onAction={(action, title) => setPending({ action, title })}
          />
          <HistorySection fleetId={fleetId} zone={zone} />
        </>
      ) : null}

      {pending ? (
        <PreviewApplyDialog
          fleetId={fleetId}
          zone={zone}
          action={pending.action}
          title={pending.title}
          onClose={() => setPending(null)}
        />
      ) : null}
    </div>
  );
}

// ---- Email ------------------------------------------------------------------

function EmailSection({
  posture,
  canEdit,
  onChoose,
}: {
  posture: { preset: EmailPresetId | null; spf: string | null };
  canEdit: boolean;
  onChoose: (action: DnsAction, title: string) => void;
}) {
  const [otherOpen, setOtherOpen] = useState(false);

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <h4 className="text-sm font-medium text-breaker-ink">Email</h4>
        {posture.preset ? (
          <Badge tone="success">{EMAIL_PRESET_LABELS[posture.preset]}</Badge>
        ) : (
          <Badge tone="neutral">Not set up</Badge>
        )}
      </div>
      <p className="text-xs text-breaker-mute">
        {posture.preset
          ? 'Mail records are live. Switching providers replaces them cleanly — your SPF is merged into one record, never duplicated.'
          : 'Send and receive mail at your domain. Pick your provider and we set the MX and SPF records for you.'}
      </p>

      {canEdit ? (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onChoose({ kind: 'email_preset', preset: 'google-workspace' }, 'Set up Google Workspace email')}
          >
            Google Workspace
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onChoose({ kind: 'email_preset', preset: 'microsoft-365' }, 'Set up Microsoft 365 email')}
          >
            Microsoft 365
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setOtherOpen(true)}>
            Other provider
          </Button>
        </div>
      ) : null}

      {otherOpen ? (
        <OtherEmailForm
          onCancel={() => setOtherOpen(false)}
          onSubmit={(action) => {
            setOtherOpen(false);
            onChoose(action, 'Set up email (other provider)');
          }}
        />
      ) : null}
    </section>
  );
}

function OtherEmailForm({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void;
  onSubmit: (action: DnsAction) => void;
}) {
  const [exchange, setExchange] = useState('');
  const [preference, setPreference] = useState('10');
  const [spfInclude, setSpfInclude] = useState('');
  const [error, setError] = useState<string | null>(null);

  function submit(e: FormEvent) {
    e.preventDefault();
    const host = exchange.trim();
    const pref = Number(preference);
    if (!host) return setError('Enter your provider’s mail server (MX host).');
    if (!Number.isInteger(pref) || pref < 0) return setError('Priority must be a whole number.');
    onSubmit({
      kind: 'email_preset',
      preset: 'other',
      other: {
        mx: [{ exchange: host, preference: pref }],
        ...(spfInclude.trim() ? { spfInclude: spfInclude.trim() } : {}),
      },
    });
  }

  return (
    <form onSubmit={submit} className="rounded-md border border-breaker-line bg-breaker-ground-2 p-3 space-y-3">
      <Field label="Mail server (MX host)" htmlFor="mxHost">
        <Input id="mxHost" value={exchange} onChange={(e) => setExchange(e.target.value)} placeholder="mx.yourprovider.com" />
      </Field>
      <Field label="Priority" htmlFor="mxPref">
        <Input id="mxPref" value={preference} onChange={(e) => setPreference(e.target.value)} inputMode="numeric" />
      </Field>
      <Field label="SPF include (optional)" htmlFor="spfInc" hint="Your provider gives you this — e.g. spf.yourprovider.com.">
        <Input id="spfInc" value={spfInclude} onChange={(e) => setSpfInclude(e.target.value)} placeholder="spf.yourprovider.com" />
      </Field>
      {error ? <p className="text-xs text-breaker-red">{error}</p> : null}
      <div className="flex gap-2">
        <Button size="sm" type="submit">Preview</Button>
        <Button size="sm" variant="ghost" type="button" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

// ---- Records ----------------------------------------------------------------

function RecordsSection({
  records,
  zone,
  canEdit,
  onAction,
}: {
  records: DnsRecordView[];
  zone: string;
  canEdit: boolean;
  onAction: (action: DnsAction, title: string) => void;
}) {
  const [form, setForm] = useState<{ editing?: DnsRecordView } | null>(null);

  const displayName = (name: string) => (name === '@' || name === '' ? zone : `${name}.${zone}`);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-medium text-breaker-ink">DNS records</h4>
        {canEdit ? (
          <Button size="sm" variant="secondary" onClick={() => setForm({})}>
            Add record
          </Button>
        ) : null}
      </div>

      {records.length === 0 ? (
        <p className="text-sm text-breaker-mute">No records yet.</p>
      ) : (
        <TableScroll>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-2xs uppercase tracking-wide text-breaker-mute">
                <th className="py-1 pr-3">Type</th>
                <th className="py-1 pr-3">Name</th>
                <th className="py-1 pr-3">Value</th>
                <th className="py-1 pr-3">TTL</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={`${r.type}-${r.name}-${i}`} className="border-t border-breaker-line align-top">
                  <td className="py-1.5 pr-3 font-mono text-xs">{r.type}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs break-all">{displayName(r.name)}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs break-all">{recordValue(r)}</td>
                  <td className="py-1.5 pr-3 text-xs text-breaker-mute">{r.ttl ?? '—'}</td>
                  <td className="py-1.5 text-right whitespace-nowrap">
                    {r.editable && canEdit ? (
                      <span className="flex gap-2 justify-end">
                        <button
                          type="button"
                          className="text-xs text-breaker-ink-2 hover:text-breaker-identity"
                          onClick={() => setForm({ editing: r })}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="text-xs text-breaker-red hover:text-breaker-red-deep transition-colors"
                          onClick={() =>
                            onAction(
                              {
                                kind: 'custom_delete',
                                record: { type: r.type, name: r.name, value: recordValue(r) },
                              },
                              `Remove ${r.type} record`,
                            )
                          }
                        >
                          Remove
                        </button>
                      </span>
                    ) : (
                      <span className="text-2xs text-breaker-mute">
                        {r.group === 'product' ? 'Managed' : 'Platform'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}

      {form ? (
        <RecordForm
          zone={zone}
          editing={form.editing}
          onCancel={() => setForm(null)}
          onSubmit={(action, title) => {
            setForm(null);
            onAction(action, title);
          }}
        />
      ) : null}
    </section>
  );
}

function RecordForm({
  zone,
  editing,
  onCancel,
  onSubmit,
}: {
  zone: string;
  editing?: DnsRecordView | undefined;
  onCancel: () => void;
  onSubmit: (action: DnsAction, title: string) => void;
}) {
  const initialType = (editing?.type.toUpperCase() as EditableRecordType) ?? 'A';
  const [type, setType] = useState<EditableRecordType>(initialType);
  const [name, setName] = useState(editing?.name === '@' ? '' : editing?.name ?? '');
  const [value, setValue] = useState(() => {
    if (!editing) return '';
    return editing.type.toUpperCase() === 'MX'
      ? String(editing.data['exchange'] ?? '')
      : recordValue(editing);
  });
  const [ttl, setTtl] = useState(editing?.ttl ? String(editing.ttl) : '3600');
  const [preference, setPreference] = useState(
    editing && typeof editing.data['preference'] === 'number'
      ? String(editing.data['preference'])
      : '10',
  );
  const [error, setError] = useState<string | null>(null);

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const nm = name.trim() || '@';
    const val = value.trim();
    if (!val) return setError('Enter a value for the record.');
    const ttlNum = Number(ttl);
    if (!Number.isInteger(ttlNum) || ttlNum < 60) return setError('TTL must be at least 60 seconds.');

    const record = {
      type,
      name: nm,
      value: val,
      ttl: ttlNum,
      ...(type === 'MX' ? { preference: Number(preference) } : {}),
    };
    const replaces = editing
      ? {
          type: editing.type,
          name: editing.name,
          value:
            editing.type.toUpperCase() === 'MX'
              ? String(editing.data['exchange'] ?? '')
              : recordValue(editing),
        }
      : undefined;

    onSubmit(
      { kind: 'custom_upsert', record, ...(replaces ? { replaces } : {}) },
      editing ? `Update ${type} record` : `Add ${type} record`,
    );
  }

  const valuePlaceholder =
    type === 'A' ? '203.0.113.5'
    : type === 'AAAA' ? '2001:db8::1'
    : type === 'CNAME' ? 'target.example.com'
    : type === 'MX' ? 'mail.example.com'
    : 'text value';

  return (
    <form onSubmit={submit} className="rounded-md border border-breaker-line bg-breaker-ground-2 p-3 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Type" htmlFor="recType">
          <select
            id="recType"
            value={type}
            onChange={(e) => setType(e.target.value as EditableRecordType)}
            disabled={Boolean(editing)}
            className="w-full rounded-md border border-breaker-line bg-white px-3 py-2 text-sm"
          >
            {RECORD_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>
        <Field label="Name" htmlFor="recName" hint={`Blank or "@" for ${zone}.`}>
          <Input id="recName" value={name} onChange={(e) => setName(e.target.value)} placeholder="shop" disabled={Boolean(editing)} />
        </Field>
      </div>
      <Field label="Value" htmlFor="recValue">
        <Input id="recValue" value={value} onChange={(e) => setValue(e.target.value)} placeholder={valuePlaceholder} />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {type === 'MX' ? (
          <Field label="Priority" htmlFor="recPref">
            <Input id="recPref" value={preference} onChange={(e) => setPreference(e.target.value)} inputMode="numeric" />
          </Field>
        ) : null}
        <Field label="TTL (seconds)" htmlFor="recTtl">
          <Input id="recTtl" value={ttl} onChange={(e) => setTtl(e.target.value)} inputMode="numeric" />
        </Field>
      </div>
      {error ? <p className="text-xs text-breaker-red">{error}</p> : null}
      <div className="flex gap-2">
        <Button size="sm" type="submit">Preview</Button>
        <Button size="sm" variant="ghost" type="button" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

// ---- Preview → apply --------------------------------------------------------

function PreviewApplyDialog({
  fleetId,
  zone,
  action,
  title,
  onClose,
}: {
  fleetId: string;
  zone: string;
  action: DnsAction;
  title: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const preview = usePreviewDnsChange(fleetId, zone);
  const apply = useApplyDnsChange(fleetId, zone);
  const { mutate: runPreview } = preview;

  useEffect(() => {
    runPreview(action);
    // The action for a given open dialog is fixed; preview once on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runPreview]);

  const plan = preview.data?.plan;
  const noop = preview.data?.noop ?? false;

  function describeSave(r: Record<string, unknown>): string {
    const type = String(r['type'] ?? '');
    const name = r['name'] === '@' || r['name'] === '' ? zone : `${String(r['name'])}.${zone}`;
    const val =
      type === 'MX'
        ? `${r['preference'] ?? ''} ${r['exchange'] ?? ''}`.trim()
        : String(r['address'] ?? r['cname'] ?? r['value'] ?? '');
    return `${type}  ${name}  →  ${val}`;
  }
  function describeDelete(r: { type: string; name: string; value: string }): string {
    const name = r.name === '@' || r.name === '' ? zone : `${r.name}.${zone}`;
    return `${r.type}  ${name}  →  ${r.value}`;
  }

  function onApply() {
    apply.mutate(action, {
      onSuccess: (res) => {
        toast.success(res.noop ? 'Already up to date.' : 'DNS updated.');
        onClose();
      },
      onError: (err) => {
        toast.error(err instanceof ApiError ? err.message : 'Couldn’t apply the change.');
      },
    });
  }

  return (
    <Dialog open onClose={onClose} title={title} description="Here&rsquo;s exactly what will change before anything goes live.">
      {preview.isPending ? (
        <div className="py-6 flex justify-center">
          <Spinner size="sm" />
        </div>
      ) : preview.isError ? (
        <p className="text-sm text-breaker-red">
          {preview.error instanceof ApiError ? preview.error.message : 'Couldn’t work out the change.'}
        </p>
      ) : noop ? (
        <div className="space-y-4">
          <p className="text-sm text-breaker-mute">Nothing to change — this is already set up.</p>
          <div className="flex justify-end">
            <Button size="sm" variant="secondary" onClick={onClose}>Close</Button>
          </div>
        </div>
      ) : plan ? (
        <div className="space-y-4">
          {plan.spf ? (
            <p className="text-xs text-breaker-mute">
              SPF record: <span className="font-mono break-all">{plan.spf}</span>
            </p>
          ) : null}
          {plan.toSave.length > 0 ? (
            <div className="space-y-1">
              <p className="text-2xs uppercase tracking-wide text-breaker-mute">Adding / updating</p>
              <ul className="space-y-0.5">
                {plan.toSave.map((r, i) => (
                  <li key={i} className="font-mono text-xs text-breaker-ink break-all">+ {describeSave(r)}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {plan.toDelete.length > 0 ? (
            <div className="space-y-1">
              <p className="text-2xs uppercase tracking-wide text-breaker-mute">Removing</p>
              <ul className="space-y-0.5">
                {plan.toDelete.map((r, i) => (
                  <li key={i} className="font-mono text-xs text-breaker-red break-all">− {describeDelete(r)}</li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={onApply} loading={apply.isPending}>Apply change</Button>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}

// ---- History ----------------------------------------------------------------

function HistorySection({ fleetId, zone }: { fleetId: string; zone: string }) {
  const changes = useDomainDnsChanges(fleetId, zone);
  if (changes.isLoading || !changes.data || changes.data.length === 0) return null;
  return (
    <section className="space-y-2">
      <h4 className="text-sm font-medium text-breaker-ink">Recent changes</h4>
      <ul className="space-y-1">
        {changes.data.slice(0, 8).map((c) => (
          <li key={c.id} className="text-xs text-breaker-mute flex justify-between gap-3">
            <span className="text-breaker-ink-2">{c.summary}</span>
            <span className="whitespace-nowrap">{formatRelative(c.createdAt)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
